import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Loader2, Trash2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { farmAPI, produceAPI, userAPI } from '@/lib/api';
import {
  hasArea, hasGroups, isDistributor, isFarmer, isOfftaker, needsWelcome, userCoverage,
} from '@/lib/onboarding';
import BuyingAreaFields, {
  buyingAreaComplete, buyingAreaForm, buyingAreaPayload,
} from '@/components/BuyingAreaFields';
import CoverageFields, { coverageComplete } from '@/components/CoverageFields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import AppHeader from '@/components/AppHeader';
import ProduceForm, { CATEGORY_ICONS, produceAmount } from '@/components/ProduceForm';
import NIGERIA_LGAS from '@/data/nigeriaLgas.json';
import {
  NIGERIAN_FARMER_ASSOCIATIONS, OTHER, SHOW_TEST_CLUSTERS, TEST_CLUSTERS, categoryOf,
} from '@/data/produce';
import SearchSelect from '@/components/SearchSelect';
import { formatPlotSize } from '@/lib/plotFormat';

const STATE_OPTIONS = Object.keys(NIGERIA_LGAS.states).map((name) => ({
  value: name, label: name === 'Abuja' ? 'Abuja (FCT)' : name,
}));

/** Clusters for the wizard: those the support team created in /admin,
 * plus the test ones (SHOW_TEST_CLUSTERS) until real ones exist. */
function useClusterNames() {
  const [names, setNames] = useState(SHOW_TEST_CLUSTERS ? TEST_CLUSTERS : []);
  useEffect(() => {
    produceAPI.allClusters()
      .then((res) => {
        const real = res.data.clusters.map((c) => c.name);
        setNames([...real, ...(SHOW_TEST_CLUSTERS ? TEST_CLUSTERS.filter((n) => !real.includes(n)) : [])]);
      })
      .catch(() => {});
  }, []);
  return names;
}

function firstStep(user, steps) {
  if (steps[0] === 'coverage' || steps[0] === 'buying' || !hasArea(user)) return 0;
  if (steps.length > 1 && !hasGroups(user)) return 1;
  return steps.length - 1;
}

/**
 * First-time wizard, one screen at a time: your area (everyone), then
 * for farmers your cluster or associations and what you produce. Every
 * screen must be answered; DashboardLayout sends people here until
 * it is done.
 */
export default function WelcomePage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const steps = isFarmer(user) ? ['area', 'groups', 'produce']
    : isDistributor(user) ? ['coverage']
    : isOfftaker(user) ? ['buying'] : ['area'];
  const [step, setStep] = useState(() => (user ? firstStep(user, steps) : 0));
  const [saving, setSaving] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-farm-cream">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!needsWelcome(user) && !saving) return <Navigate to="/dashboard" replace />;

  const save = async (fields) => {
    setSaving(true);
    try {
      const res = await userAPI.update(user.id, fields);
      updateUser(res.data);
      return true;
    } catch (error) {
      console.error('Saving the wizard step failed', error);
      toast({ title: t('wizard.saveFailed'), variant: 'destructive' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    if (await save({ onboarding_done: true })) navigate('/dashboard', { replace: true });
  };

  const next = async (fields) => {
    const last = step === steps.length - 1;
    const ok = await save(last ? { ...fields, onboarding_done: true } : fields);
    if (!ok) return;
    if (last) navigate('/dashboard', { replace: true });
    else setStep(step + 1);
  };

  const current = steps[step];
  return (
    <div className="min-h-screen bg-farm-cream safe-area-pt">
      <AppHeader />
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 text-gray-700 font-semibold h-11 px-2 -ml-2"
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-5 h-5" /> {t('wizard.back')}
            </button>
          ) : <span />}
          <LanguagePickerButton />
        </div>

        {steps.length > 1 && (
          <div className="mb-6">
            <p className="text-sm font-semibold text-farm-primary mb-2" data-testid="wizard-step">
              {t('wizard.step', { n: step + 1, total: steps.length })}
            </p>
            <div className="flex gap-2">
              {steps.map((s, i) => (
                <div key={s} className={`h-2 flex-1 rounded-full ${i <= step ? 'bg-farm-primary' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>
        )}

        {current === 'area' && <AreaStep user={user} saving={saving} onNext={next} />}
        {current === 'coverage' && <CoverageStep user={user} saving={saving} onNext={next} />}
        {current === 'buying' && <BuyingStep user={user} saving={saving} onNext={next} />}
        {current === 'groups' && <GroupsStep user={user} saving={saving} onNext={next} />}
        {current === 'produce' && <ProduceStep user={user} saving={saving} onFinish={finish} />}
      </div>
    </div>
  );
}

function StepTitle({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
    </div>
  );
}

function NextButton({ saving, label, testId = 'wizard-next', ...props }) {
  return (
    <Button
      type="submit"
      disabled={saving}
      className="w-full h-14 text-lg font-bold bg-farm-primary hover:bg-farm-primary-dark rounded-xl"
      data-testid={testId}
      {...props}
    >
      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
    </Button>
  );
}

function AreaStep({ user, saving, onNext }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [state, setState] = useState(user.state || '');
  const [lga, setLga] = useState(user.lga || '');
  const lastStep = !isFarmer(user);

  const submit = (e) => {
    e.preventDefault();
    if (!state) return toast({ title: t('auth.register.errors.noState'), variant: 'destructive' });
    if (!lga) return toast({ title: t('auth.register.errors.noLga'), variant: 'destructive' });
    return onNext({ state, lga });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <StepTitle title={t('wizard.area.title')} subtitle={t('wizard.area.subtitle')} />
      <div className="space-y-2">
        <Label className="text-base">{t('auth.register.location.state')}</Label>
        <SearchSelect
          value={state}
          onChange={(v) => { if (v !== state) { setState(v); setLga(''); } }}
          options={STATE_OPTIONS}
          placeholder={t('auth.register.location.statePlaceholder')}
          searchPlaceholder={t('wizard.search')}
          emptyText={t('wizard.noMatch')}
          testId="wizard-state"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t('auth.register.location.lga')}</Label>
        <SearchSelect
          value={lga}
          onChange={setLga}
          disabled={!state}
          options={(NIGERIA_LGAS.states[state] || []).map((name) => ({ value: name, label: name }))}
          placeholder={state
            ? t('auth.register.location.lgaPlaceholder')
            : t('auth.register.location.pickStateFirst')}
          searchPlaceholder={t('wizard.search')}
          emptyText={t('wizard.noMatch')}
          testId="wizard-lga"
        />
      </div>
      <div className="pt-4">
        <NextButton saving={saving} label={lastStep ? t('wizard.finish') : t('wizard.next')} />
      </div>
    </form>
  );
}

/** Distributors (PR 73): the country, regions and states they cover. */
function CoverageStep({ user, saving, onNext }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [coverage, setCoverage] = useState(() => userCoverage(user));

  const submit = (e) => {
    e.preventDefault();
    if (!coverageComplete(coverage)) {
      const title = coverage.regions.length === 0
        ? t('auth.register.errors.noRegions') : t('auth.register.errors.noStates');
      toast({ title, variant: 'destructive' });
      return;
    }
    onNext({ country: coverage.country, regions: coverage.regions, states: coverage.states });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <StepTitle title={t('wizard.coverage.title')} subtitle={t('wizard.coverage.subtitle')} />
      <CoverageFields value={coverage} onChange={setCoverage} testPrefix="wizard-coverage" />
      <div className="pt-4">
        <NextButton saving={saving} label={t('wizard.finish')} />
      </div>
    </form>
  );
}

/** Offtakers (PR 75): the countries, states and local areas they buy in. */
function BuyingStep({ user, saving, onNext }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [area, setArea] = useState(() => buyingAreaForm(user.buying_area));

  const submit = (e) => {
    e.preventDefault();
    if (!buyingAreaComplete(area)) {
      toast({ title: t('wizard.buying.required'), variant: 'destructive' });
      return;
    }
    onNext({ buying_area: buyingAreaPayload(area) });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <StepTitle title={t('wizard.buying.title')} subtitle={t('wizard.buying.subtitle')} />
      <BuyingAreaFields value={area} onChange={setArea} />
      <div className="pt-4">
        <NextButton saving={saving} label={t('wizard.finish')} />
      </div>
    </form>
  );
}

function GroupsStep({ user, saving, onNext }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [clusterId, setClusterId] = useState(user.cluster_id || '');
  const clusterNames = useClusterNames();
  // A name typed before (or no longer listed) shows as "Other".
  const [clusterChoice, setClusterChoice] = useState(user.cluster_id || '');
  const clusterOptions = [
    ...clusterNames.map((name) => ({ value: name, label: name })),
    ...(clusterChoice && clusterChoice !== OTHER && !clusterNames.includes(clusterChoice)
      ? [{ value: clusterChoice, label: clusterChoice }] : []),
    { value: OTHER, label: t('wizard.groups.otherCluster') },
  ];
  const [associations, setAssociations] = useState(user.associations || []);
  const [picking, setPicking] = useState('');
  const [otherName, setOtherName] = useState('');
  const [notMember, setNotMember] = useState(Boolean(user.not_member));

  const add = (name) => {
    const clean = name.trim();
    if (clean && !associations.includes(clean) && associations.length < 10) {
      setAssociations([...associations, clean]);
    }
    setPicking('');
    setOtherName('');
    setNotMember(false);
  };

  const pick = (value) => {
    if (value === OTHER) setPicking(OTHER);
    else add(value);
  };

  const submit = (e) => {
    e.preventDefault();
    const cluster = clusterId.trim();
    if (!notMember && !cluster && associations.length === 0) {
      toast({ title: t('wizard.groups.required'), variant: 'destructive' });
      return;
    }
    onNext(notMember
      ? { not_member: true }
      : { cluster_id: cluster, associations, not_member: false });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <StepTitle title={t('wizard.groups.title')} subtitle={t('wizard.groups.subtitle')} />
      <fieldset disabled={notMember} className={`space-y-5 ${notMember ? 'opacity-50' : ''}`}>
        <div className="space-y-2">
          <Label className="text-base">{t('wizard.groups.cluster')}</Label>
          <SearchSelect
            value={clusterChoice}
            onChange={(v) => { setClusterChoice(v); if (v !== OTHER) setClusterId(v); else setClusterId(''); }}
            options={clusterOptions}
            placeholder={t('wizard.groups.clusterPlaceholder')}
            searchPlaceholder={t('wizard.search')}
            emptyText={t('wizard.noMatch')}
            testId="wizard-cluster"
          />
          {clusterChoice === OTHER && (
            <Input
              className="h-14 text-base bg-white"
              maxLength={60}
              placeholder={t('wizard.groups.clusterOtherPlaceholder')}
              value={clusterId}
              onChange={(e) => setClusterId(e.target.value)}
              data-testid="wizard-cluster-other"
            />
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-base">{t('wizard.groups.associations')}</Label>
          <Select value={picking} onValueChange={pick}>
            <SelectTrigger className="h-14 bg-white text-base text-left" data-testid="wizard-association">
              <SelectValue placeholder={t('wizard.groups.associationPlaceholder')} />
            </SelectTrigger>
            <SelectContent className="max-h-72 bg-white">
              {NIGERIAN_FARMER_ASSOCIATIONS.filter((a) => !associations.includes(a)).map((name) => (
                <SelectItem key={name} value={name} className="text-base py-3">
                  {name === OTHER ? t('wizard.groups.otherAssociation') : name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {picking === OTHER && (
            <div className="flex gap-2">
              <Input
                className="h-14 text-base bg-white flex-1"
                maxLength={120}
                placeholder={t('wizard.groups.otherPlaceholder')}
                value={otherName}
                onChange={(e) => setOtherName(e.target.value)}
                data-testid="wizard-association-other"
              />
              <Button type="button" className="h-14 bg-farm-primary" onClick={() => add(otherName)}>
                {t('wizard.groups.add')}
              </Button>
            </div>
          )}
          {associations.length > 0 && (
            <ul className="space-y-2" data-testid="wizard-association-list">
              {associations.map((name) => (
                <li key={name} className="flex items-center justify-between gap-2 bg-white rounded-xl border px-4 py-3">
                  <span className="text-sm font-medium">{name}</span>
                  <button
                    type="button"
                    onClick={() => setAssociations(associations.filter((a) => a !== name))}
                    className="text-gray-500 hover:text-red-600 p-1"
                    aria-label={t('wizard.remove', { name })}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </fieldset>

      <button
        type="button"
        onClick={() => setNotMember(!notMember)}
        className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left font-semibold ${
          notMember ? 'border-farm-primary bg-farm-primary/10' : 'border-gray-200 bg-white'}`}
        aria-pressed={notMember}
        data-testid="wizard-not-member"
      >
        <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
          notMember ? 'bg-farm-primary border-farm-primary' : 'border-gray-400'}`}>
          {notMember && <Check className="w-4 h-4 text-white" />}
        </span>
        {t('wizard.groups.notMember')}
      </button>

      <div className="pt-4">
        <NextButton saving={saving} label={t('wizard.next')} />
      </div>
    </form>
  );
}

function ProduceStep({ user, saving, onFinish }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const formatSize = (plot) => formatPlotSize(plot, t, i18n.language);

  useEffect(() => {
    farmAPI.getPlots(user.id)
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]));
  }, [user.id]);

  const add = async (fields) => {
    setAdding(true);
    try {
      const res = await farmAPI.createPlot({ ...fields, user_id: user.id, location: user.state || null });
      setItems((list) => [...list, res.data]);
      setFormKey((k) => k + 1);  // a fresh, empty form for the next one
      toast({ title: t('wizard.produce.added', { name: res.data.crop_type }) });
    } catch (error) {
      console.error('Adding produce failed', error);
      toast({ title: t('farm.toast.plotFailed'), variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const remove = async (plot) => {
    try {
      await farmAPI.deletePlot(plot.id);
      setItems((list) => list.filter((p) => p.id !== plot.id));
    } catch (error) {
      toast({ title: t('farm.toast.deleteFailed'), variant: 'destructive' });
    }
  };

  const finish = () => {
    if (items.length === 0) {
      toast({ title: t('wizard.produce.required'), variant: 'destructive' });
      return;
    }
    onFinish();
  };

  return (
    <div className="space-y-5">
      <StepTitle title={t('wizard.produce.title')} subtitle={t('wizard.produce.subtitle')} />

      {items.length > 0 && (
        <ul className="space-y-2" data-testid="wizard-produce-list">
          {items.map((plot) => {
            const Icon = CATEGORY_ICONS[categoryOf(plot)];
            return (
              <li key={plot.id} className="flex items-center gap-3 bg-white rounded-xl border px-4 py-3">
                <Icon className="w-6 h-6 text-farm-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold break-words">{plot.crop_type}</p>
                  <p className="text-sm text-gray-600">{produceAmount(plot, t, formatSize)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(plot)}
                  className="text-gray-500 hover:text-red-600 p-2"
                  aria-label={t('wizard.remove', { name: plot.crop_type })}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="bg-white rounded-2xl border p-4">
        <p className="font-semibold mb-3">
          {items.length ? t('wizard.produce.addAnother') : t('wizard.produce.addFirst')}
        </p>
        <ProduceForm
          key={formKey}
          saving={adding}
          onSubmit={add}
          submitLabel={t('wizard.produce.add')}
        />
      </div>

      <NextButton
        type="button"
        saving={saving}
        label={t('wizard.finish')}
        testId="wizard-finish"
        onClick={finish}
      />
    </div>
  );
}
